import React, { useState } from 'react';
import { Puzzle, User, Challenge } from '../types';
import { X, Swords, Mail, Copy, Check, Info, Share2 } from 'lucide-react';
import { BIBLE_BOOKS, APP_VERSION } from '../constants';

interface ChallengeModalProps {
  puzzle: Puzzle;
  user: User | null;
  onClose: () => void;
}

const ChallengeModal: React.FC<ChallengeModalProps> = ({ puzzle, user, onClose }) => {
  // Quick Challenge State
  const [question, setQuestion] = useState('');
  const [book, setBook] = useState('Genesis');
  const [chapter, setChapter] = useState('1');
  const [verse, setVerse] = useState('1');
  const [email, setEmail] = useState('');
  const [copied, setCopied] = useState(false);
  const [isGenerated, setIsGenerated] = useState(false);

  const getPayload = () => {
    try {
      const challenge: Challenge = {
        challengerName: user?.username || 'A Friend',
        puzzleId: puzzle.id,
        customQuestion: question || `Solve this puzzle of ${puzzle.title}!`,
        targetBook: book,
        targetChapter: parseInt(chapter) || 1,
        targetVerse: parseInt(verse) || 1,
        isRevenge: false
      };
      // Encode securely to support UTF-8/Unicode characters
      return btoa(unescape(encodeURIComponent(JSON.stringify(challenge))));
    } catch (err) {
      console.error("Error generating challenge payload:", err);
      return "";
    }
  };

  const getChallengeLink = () => {
    const payload = getPayload();
    if (!payload) return "";
    // Use the active testing host or build domain for dynamic link routing
    const base = window.location.origin.includes('localhost') || window.location.origin.includes('run.app')
      ? window.location.origin
      : 'https://bpq-app.pages.dev';
    return `${base}/?duel=${payload}`;
  };

  const handleEmailShare = () => {
    const link = getChallengeLink();
    const subject = encodeURIComponent(`Faith Duel Challenge Link`);
    const body = encodeURIComponent(
      `⚔️ Accept my Faith Duel challenge in Bible Puzzle Quiz!\n\n` +
      `Click or copy and paste this link to load my challenge diretly inside your installed app:\n\n` +
      `${link}`
    );
    window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
  };

  const handleNativeShare = async () => {
    const link = getChallengeLink();
    const textinvite = `⚔️ Accept my Faith Duel challenge in Bible Puzzle Quiz!\n\n` +
      `Click or copy and paste this link to load my challenge diretly inside your installed app:\n\n` +
      `${link}`;

    if (navigator.share) {
      try {
        // To avoid duplicate links on platforms that append the url to the text automatically,
        // we can pass only text containing the link, which works perfectly across all major platforms.
        await navigator.share({
          title: `Faith Duel Challenge`,
          text: textinvite
        });
      } catch (err) {
        console.log('Share canceled or failed:', err);
      }
    } else {
      copyToClipboard();
    }
  };

  const fallbackCopyText = (text: string) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.top = "0";
    textArea.style.left = "0";
    textArea.style.opacity = "0";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      const successful = document.execCommand('copy');
      if (successful) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        alert('Please select and copy the text manually.');
      }
    } catch (err) {
      alert('Please select and copy the text manually.');
    }
    document.body.removeChild(textArea);
  };

  const copyToClipboard = () => {
    const textToCopy = getChallengeLink();

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(textToCopy)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        })
        .catch(() => {
          fallbackCopyText(textToCopy);
        });
    } else {
      fallbackCopyText(textToCopy);
    }
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-blue-950/65 backdrop-blur-md">
      <div className="bg-white w-full max-w-xl rounded-[2.5rem] shadow-2xl overflow-hidden animate-in zoom-in duration-300 max-h-[90vh] flex flex-col">
        <div className="p-8 md:p-10 overflow-y-auto">
          <header className="flex justify-between items-center mb-8">
            <div className="flex items-center gap-3">
              <div className="bg-blue-100 p-3 rounded-2xl text-blue-700">
                <Swords className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-2xl font-cinzel font-bold text-gray-900 leading-none">Faith Duel</h2>
                <p className="text-xs text-blue-600 font-bold uppercase tracking-widest mt-1">Challenge Your Peers</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition cursor-pointer"><X /></button>
          </header>

          <div className="space-y-6">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2 ml-1">The Challenge Question / Clue</label>
              <textarea 
                value={question}
                onChange={(e) => { setQuestion(e.target.value); setIsGenerated(false); }}
                placeholder="Ex: Noah was a righteous man, blameless among the people of his time..."
                className="w-full p-4 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 outline-none text-gray-700 h-24 resize-none font-medium text-sm"
              />
            </div>

            <div className="grid grid-cols-3 gap-3">
               <div className="col-span-3 lg:col-span-1">
                 <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2 ml-1">Answer Book</label>
                 <select 
                   value={book}
                   onChange={(e) => { setBook(e.target.value); setIsGenerated(false); }}
                   className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-bold text-gray-700 focus:ring-2 focus:ring-blue-500 outline-none"
                 >
                   {BIBLE_BOOKS.map(b => <option key={b} value={b}>{b}</option>)}
                 </select>
               </div>
               <div>
                 <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2 ml-1">Chapter</label>
                 <input type="number" value={chapter} onChange={(e) => { setChapter(e.target.value); setIsGenerated(false); }} className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-bold focus:ring-2 focus:ring-blue-500 outline-none" />
               </div>
               <div>
                 <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2 ml-1">Verse</label>
                 <input type="number" value={verse} onChange={(e) => { setVerse(e.target.value); setIsGenerated(false); }} className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-bold focus:ring-2 focus:ring-blue-500 outline-none" />
               </div>
            </div>

            {!isGenerated ? (
              <button 
                onClick={() => setIsGenerated(true)}
                className="w-full bg-blue-700 text-white py-4.5 rounded-2xl font-bold shadow-xl active:scale-95 transition-all hover:bg-blue-800 flex items-center justify-center gap-2 cursor-pointer mt-2"
              >
                <Swords className="w-5 h-5" />
                Generate Challenge Code
              </button>
            ) : (
              <div className="pt-4 border-t border-gray-100 animate-in fade-in slide-in-from-top-4 duration-500 space-y-5">
                <p className="text-[10px] font-bold text-blue-600 uppercase tracking-widest text-center mb-1">Challenge Mode Ready!</p>
                
                {/* Raw duel code block to paste */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-[9px] font-bold uppercase tracking-wider text-gray-400">Faith Duel Challenge Link</label>
                  <div className="flex items-stretch gap-2 w-full">
                    <input 
                      id="active-challenge-link"
                      type="text" 
                      readOnly
                      value={getChallengeLink()}
                      onClick={(e) => (e.target as HTMLInputElement).select()}
                      className="flex-1 min-w-0 px-4 py-3 bg-gray-100/70 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-[10px] font-mono text-blue-600 select-all text-center"
                      title="Click to select all"
                    />
                  </div>
                </div>

                {/* Primary copy & share buttons */}
                <div className="grid grid-cols-2 gap-3">
                  <button 
                    id="btn-copy-challenge-link"
                    onClick={copyToClipboard}
                    className="flex items-center justify-center gap-2 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl transition font-bold text-xs shadow-md active:scale-95 cursor-pointer"
                  >
                    {copied ? <Check className="w-4 h-4 text-green-200 animate-pulse" /> : <Copy className="w-4 h-4" />}
                    {copied ? 'Link Copied!' : 'Copy Link'}
                  </button>
                  
                  <button 
                    id="btn-share-challenge-link"
                    onClick={handleNativeShare}
                    className="flex items-center justify-center gap-2 py-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-2xl transition font-bold text-xs shadow-md active:scale-95 cursor-pointer"
                  >
                    <Share2 className="w-4 h-4" />
                    Share Link
                  </button>
                </div>

                {/* Share via email field */}
                <div className="flex flex-col gap-1.5 pt-2">
                  <label className="text-[9px] font-bold uppercase tracking-wider text-gray-400">Send Challenge Link via Email</label>
                  <div className="flex items-stretch gap-2 w-full">
                    <input 
                      type="email" 
                      placeholder="Friend's Email address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="flex-1 min-w-0 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                    />
                    <button 
                      onClick={handleEmailShare}
                      className="bg-blue-700 text-white px-5 py-3 rounded-xl hover:bg-blue-800 transition shadow-lg active:scale-95 flex items-center justify-center shrink-0 cursor-pointer"
                    >
                      <Mail className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="mt-8 flex items-start gap-3 bg-gray-50 p-4 rounded-2xl">
            <Info className="w-5 h-5 text-gray-400 shrink-0 mt-0.5" />
            <p className="text-[10px] text-gray-500 leading-relaxed font-semibold">
              💡 <strong>Direct App Integration:</strong> Direct link opening is now fully activated! Clicking this link on a mobile device automatically searches for the installed app to load the challenge directly without pasting. If the app is missing, players can download it or choose to play on Web.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChallengeModal;
